import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import PageWrapper from '../components/PageWrapper';
import '../styles/GalleryDetailPage.css';

// Icônes SVG inline
const IconDownload = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

const IconSearch = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);

const IconClose = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const GalleryDetailPage = () => {
  const { slug } = useParams();
  const [gallery, setGallery] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  
  // Image de fallback
  const defaultImage = '/images/gallery-default.png';
  
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/gallery/slug/${slug}`);
        setGallery(response.data.gallery);
        setImages(response.data.images);
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement de la galerie:', err);
        setError('Erreur lors du chargement de la galerie');
        setLoading(false);
      }
    };
    
    fetchGallery();
  }, [slug]);
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = (imageId) => {
    console.log(`Erreur de chargement d'image ${imageId}, utilisation de l'image par défaut`);
    setImageErrors(prev => ({
      ...prev,
      [imageId]: true
    }));
  };
  
  // Obtenir l'URL de l'image thumbnail via l'API
  const getThumbnailUrl = (filename, imageId) => {
    if (!filename || imageErrors[imageId]) return defaultImage;
    
    // Utiliser la nouvelle route API avec un timestamp pour éviter le cache
    const timestamp = new Date().getTime();
    return `/api/gallery/thumbnail/${filename}?t=${timestamp}`;
  };
  
  // Ouvrir l'image dans la lightbox
  const openLightbox = (image) => {
    setSelectedImage(image);
    setLightboxOpen(true);
    // Désactiver le scroll du corps
    document.body.style.overflow = 'hidden';
  };
  
  // Fermer la lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
    // Réactiver le scroll du corps
    document.body.style.overflow = 'auto';
  };
  
  // Ouvrir le modal de téléchargement
  const openDownloadModal = () => {
    if (!selectedImage) return;
    
    setDownloadModalOpen(true);
    setEmailInput('');
    setMessage(null);
  };
  
  // Gérer le changement de l'email
  const handleEmailChange = (e) => {
    setEmailInput(e.target.value);
  };
  
  // Soumettre la demande de téléchargement
  const handleDownloadSubmit = async (e) => {
    e.preventDefault();
    
    // Validation simple de l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput)) {
      setMessage({
        type: 'error',
        text: 'Veuillez entrer une adresse email valide'
      });
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post('/api/gallery/request-download', {
        imageId: selectedImage._id,
        email: emailInput
      });
      
      // Message de succès standard
      let messageText = response.data.message;
      
      // Si un lien direct est fourni (en cas d'échec d'envoi d'email)
      if (response.data.directLink) {
        messageText += ` <a href="${response.data.directLink}" target="_blank" rel="noopener noreferrer" class="direct-download-link">Cliquez ici pour télécharger directement.</a>`;
      }
      
      setMessage({
        type: 'success',
        text: messageText,
        isHTML: !!response.data.directLink
      });
      
      // Vider le champ email
      setEmailInput('');
      
      // Fermer automatiquement le modal après un délai seulement si pas de lien direct
      if (!response.data.directLink) {
        setTimeout(() => {
          setDownloadModalOpen(false);
        }, 3000);
      }
    } catch (err) {
      console.error('Erreur lors de la demande de téléchargement:', err);
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'Erreur lors de la demande de téléchargement'
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  // Navigation entre les images dans la lightbox
  const navigateToNext = () => {
    const currentIndex = images.findIndex(img => img._id === selectedImage._id);
    const nextIndex = (currentIndex + 1) % images.length;
    setSelectedImage(images[nextIndex]);
  };
  
  const navigateToPrev = () => {
    const currentIndex = images.findIndex(img => img._id === selectedImage._id);
    const prevIndex = (currentIndex - 1 + images.length) % images.length;
    setSelectedImage(images[prevIndex]);
  };
  
  // Gérer les touches du clavier dans la lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      
      switch (e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowRight':
          navigateToNext();
          break;
        case 'ArrowLeft':
          navigateToPrev();
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, selectedImage, images]);
  
  if (loading) return (
    <PageWrapper pageType="gallery">
      <div className="loading-gallery">
        <div className="loading-spinner"></div>
        <p>Chargement de la galerie...</p>
      </div>
    </PageWrapper>
  );
  
  if (error) return (
    <PageWrapper pageType="gallery">
      <div className="error-message">
        <p>{error}</p>
        <Link to="/galeries" className="back-link">
          Retour aux galeries
        </Link>
      </div>
    </PageWrapper>
  );
  
  if (!gallery) return (
    <PageWrapper pageType="gallery">
      <div className="gallery-not-found">
        <h2>Galerie introuvable</h2>
        <p>La galerie que vous recherchez n'existe pas ou a été supprimée.</p>
        <Link to="/galeries" className="back-link">
          Retour aux galeries
        </Link>
      </div>
    </PageWrapper>
  );
  
  return (
    <PageWrapper 
      pageType="gallery"
      title={`${gallery.titre} - Galerie d'Images`}
      description={gallery.description}
      image={gallery.couverture ? `/api/gallery/thumbnail/${gallery.couverture}` : undefined}
    >
      <div className="gallery-detail-page">
        <div className="gallery-detail-header">
          <div className="breadcrumbs">
            <Link to="/">Accueil</Link> &gt;{' '}
            <Link to="/galeries">Galeries</Link> &gt;{' '}
            <span className="current">{gallery.titre}</span>
          </div>
          
          <h1 className="gallery-title">{gallery.titre}</h1>
          
          {gallery.description && (
            <p className="gallery-description">{gallery.description}</p>
          )}
        </div>
        
        {images.length === 0 ? (
          <div className="no-images-message">
            <p>Aucune image n'est disponible dans cette galerie pour le moment.</p>
            <Link to="/galeries" className="back-link">
              Retour aux galeries
            </Link>
          </div>
        ) : (
          <div className="gallery-images-grid">
            {images.map(image => (
              <div 
                key={image._id} 
                className="gallery-image-item"
                onClick={() => openLightbox(image)}
              >
                <div className="gallery-image-thumbnail">
                  <img 
                    src={getThumbnailUrl(image.miniature, image._id)} 
                    alt={image.titre}
                    onError={() => handleImageError(image._id)}
                  />
                  <div className="image-overlay">
                    <IconSearch />
                  </div>
                </div>
                <div className="gallery-image-info">
                  <h3 className="image-title">{image.titre}</h3>
                  <div className="image-metadata">
                    <span className="image-dimensions">{image.dimensions}</span>
                    <span className="image-downloads">{image.downloads} téléchargements</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Lightbox */}
      {lightboxOpen && selectedImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <IconClose />
            </button>
            
            <div className="lightbox-image-container">
              <img 
                src={getThumbnailUrl(selectedImage.miniature, selectedImage._id)}
                alt={selectedImage.titre}
                className="lightbox-image"
              />
            </div>
            
            <div className="lightbox-nav">
              <button className="lightbox-nav-prev" onClick={navigateToPrev}>
                &laquo;
              </button>
              <div className="lightbox-info">
                <h3 className="lightbox-title">{selectedImage.titre}</h3>
                {selectedImage.description && (
                  <p className="lightbox-description">{selectedImage.description}</p>
                )}
                <div className="lightbox-metadata">
                  <p className="lightbox-dimensions">{selectedImage.dimensions}</p>
                  <p className="lightbox-format">{selectedImage.format && selectedImage.format.toUpperCase()}</p>
                  <p className="lightbox-size">{selectedImage.taille}</p>
                </div>
              </div>
              <button className="lightbox-nav-next" onClick={navigateToNext}>
                &raquo;
              </button>
            </div>
            
            <button className="download-button" onClick={openDownloadModal}>
              <IconDownload /> Télécharger l'image
            </button>
          </div>
        </div>
      )}
      
      {/* Modal de téléchargement */}
      {downloadModalOpen && selectedImage && (
        <div className="download-modal-overlay">
          <div className="download-modal">
            <button className="modal-close" onClick={() => setDownloadModalOpen(false)}>
              <IconClose />
            </button>
            
            <h2>Télécharger l'image</h2>
            
            <div className="download-image-preview">
              <img 
                src={getThumbnailUrl(selectedImage.miniature, selectedImage._id)}
                alt={selectedImage.titre}
              />
            </div>
            
            <p className="download-instructions">
              Entrez votre adresse email pour recevoir un lien de téléchargement pour cette image.
              Le lien est valable pendant 7 jours.
            </p>
            
            {message && (
              <div className={`download-message ${message.type}`}>
                {message.isHTML ? (
                  <div dangerouslySetInnerHTML={{ __html: message.text }}></div>
                ) : (
                  message.text
                )}
              </div>
            )}
            
            <form onSubmit={handleDownloadSubmit} className="download-form">
              <div className="form-group">
                <label htmlFor="email">Adresse email</label>
                <input 
                  type="email"
                  id="email"
                  value={emailInput}
                  onChange={handleEmailChange}
                  placeholder="votre.email@exemple.com"
                  required
                />
              </div>
              
              <div className="form-actions">
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={submitting}
                >
                  {submitting ? 'Envoi en cours...' : 'Recevoir le lien de téléchargement'}
                </button>
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => setDownloadModalOpen(false)}
                >
                  Annuler
                </button>
              </div>
            </form>
            
            <p className="privacy-note">
              Nous utilisons votre email uniquement pour vous envoyer le lien de téléchargement.
              Nous ne partageons pas vos données avec des tiers.
            </p>
          </div>
        </div>
      )}
    </PageWrapper>
  );
};

export default GalleryDetailPage;